import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import { Box, Chip, Typography, Link, TextField, Button, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../services/apiService';

const TYPE_COLORS = {
    CURRENCY: 'success',
    REWARD: 'primary',
    GELT: 'secondary',
    XP: 'info',
    DAILY_BONUS: 'warning',
    MINIGAME: 'default',
    PRIZE_SWAP: 'error',
};

const ALL_TYPES = ['CURRENCY', 'REWARD', 'GELT', 'XP', 'DAILY_BONUS', 'MINIGAME', 'PRIZE_SWAP'];

// YYYY-MM-DD for the current UTC day — feeds <input type="date"> directly.
const todayUtcYmd = () => {
    const d = new Date();
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// "YYYY-MM-DD" → ISO at 00:00:00 UTC.
const ymdToUtcStartIso = (ymd) => new Date(`${ymd}T00:00:00.000Z`).toISOString();
// "YYYY-MM-DD" → ISO at 23:59:59.999 UTC, i.e. inclusive end of that day.
const ymdToUtcEndIso = (ymd) => new Date(`${ymd}T23:59:59.999Z`).toISOString();

const TransactionsGrid = ({ playerUid = null }) => {
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    // Date range (inclusive on both ends). Default: today UTC only.
    const [fromYmd, setFromYmd] = useState(todayUtcYmd());
    const [toYmd, setToYmd] = useState(todayUtcYmd());

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
    const [sorting, setSorting] = useState([{ id: 'createdAt', desc: true }]);
    const [columnFilters, setColumnFilters] = useState([]);

    // XP level config — fetched once. Used to compute level transition chips on
    // XP rows. Sorted ascending by threshold; level = max(level_number) where
    // threshold <= xp. Server doesn't compute this — keeps the page query a
    // single UNION ALL.
    const [xpLevels, setXpLevels] = useState([]);
    useEffect(() => {
        apiService.get('/api/xp-level-configs')
            .then((levels) => setXpLevels(
                [...levels].sort((a, b) => a.xpThreshold - b.xpThreshold)
            ))
            .catch(() => setXpLevels([]));
    }, []);

    const levelForXp = useCallback((xp) => {
        if (xp == null) return null;
        let level = 0;
        for (const cfg of xpLevels) {
            if (cfg.xpThreshold <= xp) level = cfg.levelNumber;
            else break;
        }
        return level;
    }, [xpLevels]);

    // Build the filter query string ONCE; both the grid fetch and the CSV
    // export share it so the download is guaranteed to match what the user
    // sees (minus pagination — server's export ignores page/size on purpose).
    const filterParams = useMemo(() => {
        const params = new URLSearchParams({
            from: ymdToUtcStartIso(fromYmd),
            to: ymdToUtcEndIso(toYmd),
        });
        if (playerUid) params.append('playerUid', playerUid);

        const typeFilter = columnFilters.find((f) => f.id === 'type');
        if (typeFilter && Array.isArray(typeFilter.value)) {
            typeFilter.value.forEach((t) => params.append('types', t));
        }
        return params;
    }, [fromYmd, toYmd, playerUid, columnFilters]);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams(filterParams);
        params.set('page', pagination.pageIndex);
        params.set('size', pagination.pageSize);

        apiService.get(`/api/transactions?${params.toString()}`)
            .then((page) => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [pagination, filterParams]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // CSV export — uses the SAME filter params as the grid (date range,
    // type filter, optional playerUid) but with no page/size. The browser
    // handles the download flow via the server's Content-Disposition header.
    const exportCsv = useCallback(() => {
        const a = document.createElement('a');
        a.href = `/api/transactions/export.csv?${filterParams.toString()}`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [filterParams]);

    const setQuickRange = (daysBack) => {
        const end = new Date();
        const start = new Date();
        start.setUTCDate(start.getUTCDate() - daysBack);
        const toYmdStr = (d) => {
            const yyyy = d.getUTCFullYear();
            const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
            const dd = String(d.getUTCDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };
        setFromYmd(toYmdStr(start));
        setToYmd(toYmdStr(end));
        setPagination((p) => ({ ...p, pageIndex: 0 }));
    };

    const columns = useMemo(() => {
        const cols = [
            {
                accessorKey: 'createdAt',
                header: 'When',
                size: 180,
                Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
                enableColumnFilter: false,
            },
            {
                accessorKey: 'type',
                header: 'Type',
                size: 130,
                Cell: ({ cell }) => (
                    <Chip label={cell.getValue()} color={TYPE_COLORS[cell.getValue()] || 'default'} size="small" />
                ),
                filterVariant: 'multi-select',
                filterSelectOptions: ALL_TYPES,
            },
            {
                accessorKey: 'subType',
                header: 'Action',
                size: 140,
                enableColumnFilter: false,
            },
            ...(playerUid ? [] : [{
                accessorKey: 'playerUid',
                header: 'Player',
                size: 200,
                Cell: ({ row }) => (
                    <Link component={RouterLink} to={`/players/${row.original.playerUid}`}>
                        {row.original.playerName || row.original.playerUid}
                    </Link>
                ),
                enableColumnFilter: false,
            }]),
            {
                accessorKey: 'entity',
                header: 'Entity',
                size: 160,
                enableColumnFilter: false,
            },
            {
                accessorKey: 'amountBefore',
                header: 'Before',
                size: 100,
                Cell: ({ cell }) => cell.getValue() ?? '—',
                enableColumnFilter: false,
            },
            {
                accessorKey: 'amountAfter',
                header: 'After',
                size: 100,
                Cell: ({ cell }) => cell.getValue() ?? '—',
                enableColumnFilter: false,
            },
            {
                accessorKey: 'delta',
                header: 'Δ',
                size: 90,
                Cell: ({ cell }) => {
                    const v = cell.getValue();
                    if (v == null) return '—';
                    return (
                        <Typography color={v > 0 ? 'success.main' : v < 0 ? 'error.main' : 'text.primary'}>
                            {v > 0 ? `+${v}` : v}
                        </Typography>
                    );
                },
                enableColumnFilter: false,
            },
            {
                id: 'level',
                header: 'Level',
                size: 100,
                Cell: ({ row }) => {
                    if (row.original.type !== 'XP') return '—';
                    const before = levelForXp(row.original.amountBefore);
                    const after = levelForXp(row.original.amountAfter);
                    if (after == null) return '—';
                    if (after !== before) {
                        return <Chip label={`${before} → ${after}`} color="warning" size="small" />;
                    }
                    return after;
                },
                enableColumnFilter: false,
            },
            {
                accessorKey: 'initiator',
                header: 'Initiator',
                size: 220,
                enableColumnFilter: false,
            },
        ];
        return cols;
    }, [playerUid]);

    const table = useMaterialReactTable({
        columns,
        data,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        rowCount,
        state: {
            pagination,
            sorting,
            columnFilters,
            isLoading,
            showAlertBanner: isError,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        muiToolbarAlertBannerProps: isError
            ? { color: 'error', children: 'Error loading transactions' }
            : undefined,
        initialState: {
            showColumnFilters: true,
            density: 'compact',
        },
        renderTopToolbarCustomActions: () => (
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
                <TextField
                    label="From (UTC)"
                    type="date"
                    size="small"
                    value={fromYmd}
                    onChange={(e) => { setFromYmd(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                    label="To (UTC)"
                    type="date"
                    size="small"
                    value={toYmd}
                    onChange={(e) => { setToYmd(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button size="small" variant="outlined" onClick={() => setQuickRange(0)}>Today</Button>
                <Button size="small" variant="outlined" onClick={() => setQuickRange(7)}>7d</Button>
                <Button size="small" variant="outlined" onClick={() => setQuickRange(30)}>30d</Button>
                <Button size="small" variant="outlined" onClick={() => setQuickRange(365)}>1y</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={exportCsv}
                    disabled={rowCount === 0}
                    title="Download all rows matching the current filter as CSV (ignores pagination)"
                >
                    Export CSV
                </Button>
            </Stack>
        ),
        renderDetailPanel: ({ row }) => row.original.details
            ? (
                <Box sx={{ p: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(row.original.details, null, 2)}
                </Box>
            )
            : null,
    });

    return <MaterialReactTable table={table} />;
};

export default TransactionsGrid;
